import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { ReminderRequestDto } from '../../dto/reminder-request-dto';
import { RemindersService } from '../../service/reminders.service';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { ReminderTopic } from '../../dto/ReminderTopic';
import { ReminderResponseDTO } from '../../dto/reminder-response-d-t-o';
import { ReminderUpdateDTO } from '../../dto/ReminderUpdateDTO';
import { NOTIFICATIONS, NotificationsService } from '../../../../core/service/notifications.service';
import { CabinetService } from '../../../cabinet/service/cabinet.service';
import { AuthService } from '../../../auth/service/auth.service';
import { ModalButtons, SpinnerProps } from '../../../shared/component/modal-container/modal-container.component';
import { Reminder } from '../../dto/reminder';
import { EmployeeRole, OrganizationWithAccesses } from 'src/app/core/component/hornavbar/dto/InitDataDTO';
import { takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';
import { Organization } from 'src/app/domain/organization/dto/Organization';

@Component({
  selector: 'app-reminders',
  templateUrl: './reminders.component.html',
  styleUrls: ['./reminders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class RemindersComponent implements OnChanges, OnInit, OnDestroy {
  @Input() organizationsWithRole: OrganizationWithAccesses[];
  @Input() selectedOrganization: Organization | null;
  @Input() reminder;
  @Output() closeEmitter = new EventEmitter<void>();
  @Output() hasActiveRemindersEmitter = new EventEmitter<boolean>();

  _destroyStream: Subject<void> = new Subject<void>();
  activeRemindersNumber: number;
  reminders: ReminderResponseDTO[];
  filteredReminders: {
    pending: ReminderResponseDTO[];
    completed: ReminderResponseDTO[];
  };

  privacyTypes = ['Shared & personal', 'Shared', 'Personal'];
  privacyType = 'Shared & personal';
  selectedOrg: Organization;
  currentUser: string;
  isAdminInsideOrg: boolean;
  isAdmin: boolean;
  forUpdateBody: ReminderRequestDto;
  selectedOrgList: SelectedOrgList[] = [];
  selectedOrgUuids: string[] = [];
  allSelected = false;
  spinnerProps: SpinnerProps = { notActiveOnApply: true };
  statusList = ['Completed'];
  statusListCompleted = ['Active'];
  reminderFundName: string;
  textListForDeleteReminder: string[];
  reminderForDelete: Reminder;
  show = new ShowReminders();

  modalButtons: ModalButtons = {
    close: true,
    apply: 'Create notification',
    applySize: 'button-size-12',
  };

  constructor(private reminderService: RemindersService,
    private organizationsService: OrganizationsService,
    private notificationService: NotificationsService,
    private cabinetService: CabinetService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedOrganization) {
      this.selectedOrgList = [];
      this.organizationsWithRole.forEach((orgWithRole) => {
        if (!orgWithRole || !this.selectedOrganization) {
          return;
        }
        if (orgWithRole.userAccesses.fmsAccess === EmployeeRole.admin) {
          this.selectedOrgList.push({
            selected: true,
            uuid: orgWithRole.organization.uuid,
            name: orgWithRole.organization.name,
          });
        } else if (orgWithRole.userAccesses.fmsAccess === EmployeeRole.externalAdmin) {
          this.selectedOrgList.push({
            selected: orgWithRole.organization.uuid === this.selectedOrganization.uuid,
            uuid: orgWithRole.organization.uuid,
            name: orgWithRole.organization.name,
          });
        }
      });
      this.get();
    }
  }

  ngOnInit(): void {
    this.initEmptyFilteredReminders();
    this.subscription();
    this.setSelectedOrg();
  }

  toggleSelectAll(): void {
    this.allSelected = !this.allSelected;
    this.selectedOrgList.forEach((org) => org.selected = this.allSelected);
    this.get();
  }

  selectOne(i: number): void {
    this.selectedOrgList[i].selected = !this.selectedOrgList[i].selected;
    this.get();
  }

  selectCurrentOrg(): void {
    this.selectedOrgList.forEach((org) => {
      org.selected = org.uuid === this.selectedOrg.uuid;
    });
    this.allSelected = false;
    this.get();
  }

  subscription(): void {
    this.organizationsService.selected$.pipe(takeUntil(this._destroyStream)).subscribe((org) => {
      if (!org) {
        return;
      }
      // Only if fund is given => load all reminders and load current user to check his/her name for the filter.
      this.selectedOrg = org;
      this.get();
      this.cabinetService.profile.pipe(takeUntil(this._destroyStream)).subscribe((profile) => {
        if (profile) {
          this.currentUser = profile.name + (profile.secondName ? ' ' + profile.secondName : '');
        }
      });

    });
    this.authService.fmsAccessInsideOrganizationForServices$.pipe(takeUntil(this._destroyStream)).subscribe((userType) => {
      this.isAdminInsideOrg = userType.toString().includes('admin');
    });
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream)).subscribe((userType) => {
      this.isAdmin = userType === 'admin';
    });
  }

  async get(): Promise<void> {
    if (!this.selectedOrg || !this.selectedOrgList.length) {
      return;
    }
    this.resetReminders();
    this.selectedOrgUuids = this.selectedOrgList.filter((org) => org.selected).map((org) => org.uuid);
    const data = await firstValueFrom(this.reminderService.get(this.selectedOrgUuids));
    if (!data?.length) {
      this.initEmptyFilteredReminders();
      return;
    }
    // Parse from enum to the client-readable value
    data.map((a) => {
      a.topic = ReminderTopic[a.topic] as unknown as ReminderTopic;
    });
    this.reminders = data;
    this.filterReminders();
    this.checkIfActiveRemindersDisplayed();
  }

  add(body: ReminderRequestDto): void {
    body.deadline = body.deadline ? new Date(body.deadline) : null;
    body.reminderDate = new Date(body.reminderDate);
    body.topic = this.getReminderTopicKey(body.topic);
    const org = this.organizationsWithRole.find((el) => el.organization.funds.find((fund) => fund.uuid === body.fundUUID));
    const selectedOrg = this.selectedOrgList.find((o) => o.uuid === org.organization.uuid);
    this.reminderService.add(body).pipe(takeUntil(this._destroyStream)).subscribe(() => {
      selectedOrg.selected = true;
      this.get();
      this.notificationService.success(NOTIFICATIONS.ADD_NEW_NOTIFICATION_SUCCESS);
    }, () => this.notificationService.error(NOTIFICATIONS.CREATE_NOTIFICATION_ERROR));
    this.show.modalAdd = false;

  }

  activeFieldIsTriggered(responseDTO: ReminderResponseDTO): void {
    responseDTO.isCompleted = !responseDTO.isCompleted;
    const org = this.organizationsWithRole.find((el) => el.organization.funds.find((f) => f.name === responseDTO.fund));
    const fund = org.organization.funds.filter((f) => f.name === responseDTO.fund)[0];
    responseDTO.fundUUID = fund.uuid;
    this.update(responseDTO);
  }

  update(body: ReminderResponseDTO): void {
    const dto = new ReminderUpdateDTO(body.id, body.isPersonal, body.deadline,
      body.reminderDate, body.topic, body.description, body.isCompleted);
    const fundUUID = body.fundUUID;
    dto.topic = this.getReminderTopicKey(body.topic);
    const org = this.organizationsWithRole.find((el) => el.organization.funds.find((fund) => fund.uuid === fundUUID));
    const selectedOrg = this.selectedOrgList.find((o) => o.uuid === org.organization.uuid);
    this.reminderService.update(dto, fundUUID).pipe(takeUntil(this._destroyStream)).subscribe(() => {
      selectedOrg.selected = true;
      this.get();
      this.notificationService.success(NOTIFICATIONS.UPDATE_NOTIFICATION_SUCCESS);
    }, () => this.notificationService.error(NOTIFICATIONS.UPDATE_NOTIFICATION_ERROR));
    this.show.modalUpdate = false;
  }

  deleteById(id: number): void {
    this.reminderService.delete(id).pipe(takeUntil(this._destroyStream)).subscribe(() => {
      this.reminders = this.reminders.filter((n) => n.id !== id);
      this.filterReminders();
      this.notificationService.success(NOTIFICATIONS.DELETE_NOTIFICATION_SUCCESS);
      this.cdr.detectChanges();
    });
  }

  filterReminders(type?: string): void {
    if (type) this.privacyType = type;
    switch (this.privacyType) {
      case 'Shared & personal':
        this.splitReminders(this.reminders);
        break;
      case 'Shared':
        this.splitReminders(this.reminders.filter((n) => !n.isPersonal));
        break;
      case 'Personal':
        this.splitReminders(this.reminders.filter((n) => n.isPersonal));
        break;
    }
  }

  splitReminders(reminders: Reminder[]): void {
    this.initEmptyFilteredReminders();
    if (!reminders?.length) {
      return;
    }
    this.show.table = true;
    reminders.forEach((r) => {
      const [daysToDeadline, daysToReminder] = [this.daysDiffUntilNow(r.deadline), this.daysDiffUntilNow(r.reminderDate)];
      r.daysToDeadline = daysToDeadline;
      r.daysToReminder = daysToReminder;
      r.daysToDeadlineStr = this.daysDiffString(daysToDeadline);
      r.daysToReminderStr = this.daysDiffString(daysToReminder);
      if (r.isCompleted) {
        this.filteredReminders.completed.push(r);
      } else {
        this.filteredReminders.pending.push(r);
      }
    });
  }

  toRequestDto(body: ReminderResponseDTO): void {
    this.forUpdateBody = new ReminderRequestDto(body.id, body.isPersonal, undefined, body.deadline,
      body.reminderDate, body.topic, body.description, body.isCompleted);
    const fund = body.fund;
    this.reminderFundName = fund;

  }

  initEmptyFilteredReminders(): void {
    this.filteredReminders = {
      pending: [],
      completed: [],
    };
    this.show.table = false;
    this.hasActiveRemindersEmitter.emit(false);
  }

  showModalDeleteReminder(reminder: Reminder): void {
    this.show.modalDelete = true;
    this.reminderForDelete = reminder;
  }

  closeModalDeleteReminder(withUpdate: boolean): void {
    if (withUpdate) {
      this.deleteById(this.reminderForDelete.id);
    }
    this.show.modalDelete = false;
  }

  getReminderTopicKey(value: string|ReminderTopic): ReminderTopic | undefined {
    const entries = Object.entries(ReminderTopic);
    for (const [key, val] of entries) {
      if (val as string === value) {
        return key as ReminderTopic;
      }
    }
    return undefined;
  }

  /**
   *  calculate the number of the fetched reminders without snoozed ones.
   */
  private checkIfActiveRemindersDisplayed(): void {
    this.activeRemindersNumber = this.reminders.
      filter((r) => !r.isCompleted && new Date(r.reminderDate)
        .getTime() < new Date().getTime()).length;
    this.hasActiveRemindersEmitter.emit(Boolean(this.activeRemindersNumber));
  }

  private resetReminders(): void {
    this.reminders = [];
    this.activeRemindersNumber = 0;
  }

  private daysDiffUntilNow(date: Date): number {
    const daysDiff = (new Date(date).getTime() - new Date().getTime()) / (86400 * 1000);
    return daysDiff > 0 ? Math.trunc(daysDiff + 1) : Math.trunc(daysDiff);
  }

  private daysDiffString(numberOfDays: number): string {
    if (numberOfDays === 0) {
      return 'Today';
    }
    if (numberOfDays > 0) {
      return `${numberOfDays} days left`;
    }
    return `${-numberOfDays} days passed`;
  }

  private setSelectedOrg(): void {
    if (this.organizationsWithRole && this.organizationsWithRole.length && this.selectedOrg) {
      this.selectedOrgList = [];
      this.organizationsWithRole.forEach((orgWithRole) => {
        if (orgWithRole.userAccesses.fmsAccess === EmployeeRole.admin) {
          this.selectedOrgList.push({
            selected: true,
            uuid: orgWithRole.organization.uuid,
            name: orgWithRole.organization.name,
          });
        } else if (orgWithRole.userAccesses.fmsAccess === EmployeeRole.externalAdmin) {
          this.selectedOrgList.push({
            selected: orgWithRole.organization.uuid === this.selectedOrg.uuid,
            uuid: orgWithRole.organization.uuid,
            name: orgWithRole.organization.name,
          });

        }
      });
      this.get();
    }
  }
}

export interface SelectedOrgList {
  selected: boolean;
  uuid: string;
  name: string;
}

class ShowReminders {
  modalAdd = false;
  modalUpdate = false;
  modalDelete = false;
  pending = true;
  completed = false;
  table = true;
}
